import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'clsx';

import { CACHE } from '../common/constants/playlists';
import { CHANNEL_LIST_PATH, PAGE_TO_ROUTE_TAGS_MAPPER } from '../common/constants/routePath';
import {
  DESIGN_BACKGROUND,
  DESIGN_INLINE,
  DESIGN_INLINE_VERTICAL,
  DESIGN_STANDARD,
  DESIGN_VERTICAL,
} from './constants/carousels';
import { TYPE_MAIN_BOTTOM, TYPE_MAIN_TOP } from '@/modules/common/components/Ad/constants';

import { ChannelType } from '@/modules/common/types/channel';

import { useSelectedChipFromQuery } from '../common/components/Chips/helpers/useSelectedChipFromQuery';
import { filterVideosByChip } from '../common/helpers/chips';
import { scrollToPublisherAnchor } from '../common/helpers/dom';
import * as commonSelectors from '../common/redux/selectors/common';
import { channelsFetchStartAction } from '../common/redux/slices/common';
import { useTranslate } from '@/modules/common/helpers/i18n';
import { isMobileUserAgent } from '@/modules/common/helpers/userAgent';

import ChannelConfiguration from '@/modules/ChannelList/components/ChannelConfiguration/ChannelConfiguration';
import Ad from '@/modules/common/components/Ad/Ad';
import Chips from '@/modules/common/components/Chips/Chips';
import ScrollObserver from '../common/components/ScrollObserver/ScrollObserver';
import BackgroundCarousel from './components/BackgroundCarousel/BackgroundCarousel';
import DefaultCarousel from './components/DefaultCarousel/DefaultCarousel';
import Header from './components/Header/Header';
import InlineCarousel from './components/InlineCarousel/InlineCarousel';
import InlineVerticalCarousel from './components/InlineVerticalCarousel/InlineVerticalCarousel';
import VerticalCarousel from './components/VerticalCarousel/VerticalCarousel';
import { Stack } from '@/mui/components';

import styles from './ChannelList.module.scss';

function ChannelListPage() {
  const dispatch = useDispatch();
  const selectedChip = useSelectedChipFromQuery();
  const search = useSelector(commonSelectors.searchSelector);
  const channels = useSelector(commonSelectors.channelsSelector);
  const tagTypes = useSelector(commonSelectors.tagTypesSelector);
  const tagPages = useSelector(commonSelectors.tagPagesSelector);
  const t = useTranslate();

  const carouselCollection = {
    [DESIGN_STANDARD]: DefaultCarousel,
    [DESIGN_BACKGROUND]: BackgroundCarousel,
    [DESIGN_INLINE]: InlineCarousel,
    [DESIGN_VERTICAL]: VerticalCarousel,
    [DESIGN_INLINE_VERTICAL]: InlineVerticalCarousel,
  };

  useEffect(() => {
    scrollToPublisherAnchor();

    const unknownChannels = channels.filter((chl) => !chl.playlistLoaded && !chl.disabled);

    if (unknownChannels.length) {
      dispatch(
        channelsFetchStartAction({
          cacheUsage: CACHE,
          channels: unknownChannels,
        }),
      );
    }
  }, []);

  const channelsToShow = channels.filter((channel) => !channel.disabled);

  const filteredChannelsToShow =
    tagPages.some((page) => PAGE_TO_ROUTE_TAGS_MAPPER[page].includes(CHANNEL_LIST_PATH)) && selectedChip
      ? channelsToShow
          .reduce((acc, channel) => {
            const playlist = filterVideosByChip(channel.playlist, tagTypes, selectedChip) as ChannelType['playlist'];

            if (playlist.length) {
              return acc.concat({ ...channel, playlist });
            }

            return acc;
          }, [] as ChannelType[])
          .filter(Boolean)
      : channelsToShow;

  return (
    <div
      data-a="page-channel-list"
      className={classNames(styles.Wrapper, {
        [styles.Wrapper___withoutSearch]: !search,
      })}
    >
      {!isMobileUserAgent && search && <Header />}
      <Stack fullWidth spacing={4}>
        {tagPages.some((page) => PAGE_TO_ROUTE_TAGS_MAPPER[page].includes(CHANNEL_LIST_PATH)) &&
          !!channelsToShow.length && (
            <Chips
              loading={!channelsToShow.some((channel) => channel.playlistLoaded)}
              videos={channelsToShow.flatMap((channel) => channel.playlist)}
            />
          )}
        <Ad type={TYPE_MAIN_TOP} />
        <Stack fullWidth spacing={8} aria-label={t.videosByChannels.text()}>
          {filteredChannelsToShow.map((channel) => {
            const CarouselComponent = carouselCollection[channel.design];

            return (
              <ChannelConfiguration key={channel.name} id={channel.id} design={channel.design}>
                <CarouselComponent
                  titleColor={channel.titleColor}
                  descColor={channel.descColor}
                  vidTitleColor={channel.vidTitleColor}
                  vidInfoColor={channel.vidInfoColor}
                  bg={channel.bg}
                  bgLogo={channel.bgLogo}
                  channel={channel}
                />
              </ChannelConfiguration>
            );
          })}
        </Stack>
      </Stack>
      <Ad type={TYPE_MAIN_BOTTOM} />
      <ScrollObserver />
    </div>
  );
}

export default ChannelListPage;
